import { isPlatformBrowser } from '@angular/common';
import { NgTemplateOutlet } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  numberAttribute,
  output,
  PLATFORM_ID,
  signal,
  TemplateRef
} from '@angular/core';
import { UmssCardComponent } from '../card/card.component';
import { UmssDialogComponent } from '../dialog/dialog.component';
import { UmssIconComponent } from '../icon/icon.component';
import { UmssSkeletonComponent } from '../skeleton/skeleton.component';
import { UmssProgressRingComponent } from '../progress-ring/progress-ring.component';
import { SetupStep } from './setup-card.models';
import { UmssSetupStepComponent } from './setup-step/setup-step.component';

/** How long the completion state stays up before the card fades out. */
const CELEBRATE_MS = 3000;
/** Must match the fade transition in the stylesheet. */
const FADE_MS = 350;

/** Drives the confetti burst shown alongside the completion message. */
const CONFETTI_PIECES = Array.from({ length: 20 }, (_, index) => index);

/**
 * Checklist card showing setup progress and the steps still to complete.
 *
 * Desktop: an always-expanded sidebar card.
 * Mobile: a collapsed header that opens the checklist in a bottom sheet.
 *
 * The card owns presentation and which step is selected; it owns no domain
 * logic. Callers supply the steps and a template for the selected step's
 * detail view, which replaces the checklist in whichever container is live.
 */
@Component({
  selector: 'umss-setup-card',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    UmssCardComponent,
    UmssDialogComponent,
    UmssIconComponent,
    UmssProgressRingComponent,
    UmssSetupStepComponent,
    UmssSkeletonComponent
  ],
  templateUrl: './setup-card.component.html',
  styleUrl: './setup-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UmssSetupCardComponent {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  steps = input.required<readonly SetupStep[]>();
  title = input('Profile Setup');
  /** Explains what the checklist unblocks. Hidden when empty. */
  description = input('');
  loading = input(false, { transform: booleanAttribute });

  /**
   * Highlights the description in amber. Used to draw attention after a user
   * tries an action the checklist is still blocking.
   */
  attention = input(false, { transform: booleanAttribute });

  completionTitle = input('Profile Setup Complete!');
  completionMessage = input('');

  /**
   * Holds the card open while the caller still needs it — typically because a
   * dialog it opened is on screen. Without this the card can start hiding
   * behind an open modal, leaving the user wondering where it went.
   */
  holdOpen = input(false, { transform: booleanAttribute });

  /** Emits when the card removes itself: true if it faded, false if immediate. */
  dismissed = output<boolean>();

  /** Viewport width at or above which the card is always expanded. */
  desktopBreakpoint = input(1024, { transform: numberAttribute });

  /**
   * Rendered in place of the checklist once a step is selected, receiving that
   * step as its implicit context. A template rather than projected content
   * because the detail appears in two mutually exclusive places (the desktop
   * card body and the mobile sheet) and `ng-content` only projects once.
   */
  detailTemplate = input<TemplateRef<{ $implicit: SetupStep }> | null>(null);

  /**
   * The step being shown in detail, or null for the checklist. Writable so a
   * detail view can navigate back by clearing it.
   */
  activeStep = model<SetupStep | null>(null);

  stepClick = output<SetupStep>();

  private readonly viewportWidth = signal(0);
  private readonly expandedOnMobile = signal(false);

  /** Removed from the DOM entirely — the end state of the completion flow. */
  protected readonly gone = signal(false);
  /** Mid-fade, before removal. */
  protected readonly fading = signal(false);
  protected readonly confettiPieces = CONFETTI_PIECES;

  private initialLoadDone = false;
  private hideTimer?: ReturnType<typeof setTimeout>;
  private removeTimer?: ReturnType<typeof setTimeout>;

  readonly completedCount = computed(
    () => this.steps().filter((step) => step.completed).length
  );
  readonly totalCount = computed(() => this.steps().length);
  readonly allCompleted = computed(
    () => this.totalCount() > 0 && this.completedCount() === this.totalCount()
  );

  readonly progressPercent = computed(() => {
    const total = this.totalCount();
    if (total === 0) return 0;
    return Math.round((this.completedCount() / total) * 100);
  });

  protected readonly isDesktop = computed(
    () => this.viewportWidth() >= this.desktopBreakpoint()
  );

  protected readonly showDetail = computed(
    () => this.activeStep() !== null && this.detailTemplate() !== null
  );

  /** Desktop shows the body inline; mobile opens it in the sheet instead. */
  protected readonly sheetOpen = computed(
    () => !this.isDesktop() && (this.expandedOnMobile() || this.showDetail())
  );

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const onResize = () => this.viewportWidth.set(window.innerWidth);
      onResize();
      window.addEventListener('resize', onResize);
      this.destroyRef.onDestroy(() =>
        window.removeEventListener('resize', onResize)
      );
    }

    effect(() => {
      const done = this.allCompleted();
      const loading = this.loading();
      const held = this.holdOpen();

      if (loading) return;

      if (!this.initialLoadDone) {
        this.initialLoadDone = true;
        // Already complete when the page opened — there is nothing to
        // celebrate, so skip straight to gone with no animation.
        if (done) {
          this.gone.set(true);
          this.dismissed.emit(false);
        }
        return;
      }

      if (done && !held && !this.gone() && !this.fading()) {
        this.startHideSequence();
      }
    });

    this.destroyRef.onDestroy(() => {
      clearTimeout(this.hideTimer);
      clearTimeout(this.removeTimer);
    });
  }

  /** Shows the completion state briefly, then fades the card out. */
  private startHideSequence(): void {
    this.hideTimer = setTimeout(() => {
      this.fading.set(true);
      this.dismissed.emit(true);
      this.removeTimer = setTimeout(() => this.gone.set(true), FADE_MS);
    }, CELEBRATE_MS);
  }

  protected onHeaderClick(): void {
    // Desktop has nothing to toggle — the body is always visible.
    if (this.isDesktop()) return;

    if (this.showDetail()) {
      this.activeStep.set(null);
      return;
    }
    this.expandedOnMobile.update((expanded) => !expanded);
  }

  protected onStepClick(step: SetupStep): void {
    // Steps that navigate away rather than open a panel leave the checklist up.
    if (step.opensDetail !== false) {
      this.activeStep.set(step);
    }
    this.stepClick.emit(step);
  }

  protected onSheetClose(): void {
    this.expandedOnMobile.set(false);
    this.activeStep.set(null);
  }
}
